import { useEffect, useMemo, useState } from "react";
import {
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiSpacer,
  EuiIcon,
  EuiBadge,
  EuiHealth,
  EuiAccordion,
  EuiTable,
  EuiTableHeader,
  EuiTableHeaderCell,
  EuiTableBody,
  EuiTableRow,
  EuiTableRowCell,
  EuiAvatar,
  EuiLink,
  EuiToolTip,
  EuiSwitch,
  EuiLoadingSpinner,
} from "@elastic/eui";
import {
  CUSTOMER_SWITCH_VALUE,
  NetBoxDeviceWithInventory,
  useAnketaAuth,
  useApi,
  useToast,
} from "../../../../";
import { ComplexQuestionEditorProps } from "../../../";
import { AnketaResponse } from "../../../../api/types";
import { useAnketaI18n } from "src/internationalization";

// Colours resolve through questionnaire-theme.css so the editor follows the
// light/dark stylesheet swap (hex literals here stayed light in dark mode).
const T = {
  blue: "var(--aq-primary)",
  blueDim: "var(--aq-primary-tint)",
  surface: "var(--aq-surface)",
  border: "var(--aq-border)",
  borderStrong: "var(--aq-border-strong)",
  text: "var(--aq-text)",
  textMuted: "var(--aq-text-muted)",
  card: "var(--aq-card)",
  radius: "8px",
  radiusSm: "5px",
  shadow: "var(--aq-shadow)",
  shadowMd: "var(--aq-shadow-md)",
};

// Hex on purpose: EuiAvatar validates its colour as #rgb / #rrggbb and THROWS
// on anything else - passing the CSS var() from T.blue crashed the whole
// questionnaire. The avatar derives its text colour from this background, so
// one hex works in both colour modes.
const AVATAR_BLUE = "#0070F3";

const metaRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  color: T.textMuted,
};

const sectionBox: React.CSSProperties = {
  border: `1px solid ${T.border}`,
  borderRadius: T.radius,
  overflow: "hidden",
  background: T.card,
};

const statusColorMap: Record<string, string> = {
  active: "success",
  planned: "warning",
  staged: "warning",
  failed: "danger",
  inventory: "subdued",
  decommissioning: "danger",
};

const tagStyle = (color: string): string => `#${color}`;

export interface NetBoxDeviceEditorProps extends ComplexQuestionEditorProps {
  type: "server" | "switch" | "dac" | "probe";
}

export const NetBoxDeviceEditor = ({
  question,
  setValue,
  invalid,
  type,
}: NetBoxDeviceEditorProps): JSX.Element => {
  const { keycloak } = useAnketaAuth();
  const { Get } = useApi();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();

  const [devices, setDevices] = useState<NetBoxDeviceWithInventory[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<
    NetBoxDeviceWithInventory[]
  >([]);
  const [selectedOptions, setSelectedOptions] = useState<
    EuiComboBoxOptionOption<NetBoxDeviceWithInventory>[]
  >([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  // NetBox has no free device of this kind (all tagged "opsanioinuse").
  const [noneFree, setNoneFree] = useState<boolean>(false);
  // Lazily-fetched inventory/interface detail, keyed by device id. The list
  // request stays lightweight; detail is loaded only for selected devices.
  const [detailMap, setDetailMap] = useState<
    Record<number, Partial<NetBoxDeviceWithInventory>>
  >({});
  const [detailLoading, setDetailLoading] = useState<Set<number>>(new Set());
  const [manufacturerLogos, setManufacturerLogos] = useState<
    Record<string, string>
  >({});
  const [useCustomerSwitch, setUseCustomerSwitch] = useState<boolean>(
    () => question.fact?.raw?.toString() === CUSTOMER_SWITCH_VALUE,
  );

  const canFetch = useMemo<boolean>(() => {
    if (keycloak) return typeof keycloak.token === "string";
    return false;
  }, [keycloak]);

  const getDevices = async (value: string) => {
    let baseParams = "";
    let basePath = "";
    switch (type) {
      case "server":
        // Lightweight list for the combobox; inventory/interface detail is
        // lazy-loaded per device on selection (see loadDeviceDetail).
        baseParams = "getFree=true&fetchAll=true";
        basePath = "devices";
        break;
      case "switch":
        baseParams = "getFree=true&limit=10";
        basePath = "devices/switches";
        break;
      case "dac":
        baseParams = "getFree=true&limit=10";
        basePath = "devices/dac";
        break;
      case "probe":
        baseParams = "getFree=true&limit=10";
        basePath = "devices/probes";
        break;
    }
    const path =
      value.trim() !== ""
        ? `v2/netbox/${basePath}?${baseParams}&ids=${value.trim()}`
        : `v2/netbox/${basePath}?${baseParams}`;
    try {
      const response = await Get<AnketaResponse<NetBoxDeviceWithInventory[]>>({
        path,
        responseType: "json",
        token: keycloak.token,
      });
      if (response.success && response.status === 204) {
        // Not an error: NetBox answered, there is just nothing free of this
        // kind (every matching device carries the "opsanioinuse" tag). Say
        // so in the picker instead of raising a "NetBox error" toast.
        setDevices([]);
        setNoneFree(true);
        setIsLoaded(true);
      } else if (response.success && response.data) {
        console.log("Devices loaded successfully", response.data.data.length);
        setNoneFree(false);
        setDevices(response.data.data);
      } else {
        const errorMsg =
          response.error?.message ||
          translate("anketa.questionnaire.editor.device.error.retrieve", {
            defaultMessage: "Failed to retrieve devices from NetBox",
          });
        console.warn("API Error:", errorMsg);
        setIsLoaded(true);
        addToast({
          title: translate(
            "anketa.questionnaire.editor.device.error.netbox.title",
            { defaultMessage: "NetBox Error" },
          ),
          color: "danger",
          text: errorMsg,
        });
        setDevices([]);
      }
    } catch (error) {
      console.error("Critical Network Error:", error);
      setIsLoaded(true);
      addToast({
        title: translate(
          "anketa.questionnaire.editor.device.error.connection.title",
          { defaultMessage: "Connection Failed" },
        ),
        color: "danger",
        text: translate(
          "anketa.questionnaire.editor.device.error.connection.text",
          {
            defaultMessage:
              "Could not reach the API server. Please check your connection.",
          },
        ),
      });
      setDevices([]);
    }
  };

  const loadDeviceDetail = async (deviceIds: number[]) => {
    if (type !== "server") return;
    const missing = deviceIds.filter(
      (id) => !detailMap[id] && !detailLoading.has(id),
    );
    if (missing.length === 0) return;

    setDetailLoading((prev) => {
      const next = new Set(prev);
      missing.forEach((id) => next.add(id));
      return next;
    });

    try {
      const path = `v2/netbox/devices?getFree=true&includeInventory=true&includeInterfaces=true&limit=1&ids=${missing.join(",")}`;
      const response = await Get<AnketaResponse<NetBoxDeviceWithInventory[]>>({
        path,
        responseType: "json",
        token: keycloak.token,
      });
      if (response.success && response.data) {
        const fetched = response.data.data ?? [];
        setDetailMap((prev) => {
          const next = { ...prev };
          for (const id of missing) {
            const found = fetched.find((d) => d.id === id);
            next[id] = found
              ? { interfaces: found.interfaces, inventory: found.inventory }
              : {};
          }
          return next;
        });
      }
    } catch (error) {
      console.error("[NetBox] Failed to load device detail", error);
    } finally {
      setDetailLoading((prev) => {
        const next = new Set(prev);
        missing.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const fetchManufacturerLogo = async (
    manufacturerName: string,
  ): Promise<string | null> => {
    if (!manufacturerName) return null;
    try {
      const { data } = await Get({
        path: `v2/images/${encodeURIComponent(manufacturerName.toLowerCase())}`,
        responseType: "json",
        token: keycloak.token,
      });
      const images = data as Array<{ url: string; name: string }>;
      return images?.[0]?.url ?? null;
    } catch {
      return null;
    }
  };

  const fetchAllDevices = async (deviceList: NetBoxDeviceWithInventory[]) => {
    const names = [
      ...new Set(
        deviceList.map((d) => d.device_type.manufacturer.name).filter(Boolean),
      ),
    ];
    const entries = await Promise.all(
      names.map(async (name) => {
        const url = await fetchManufacturerLogo(name);
        return [name, url] as [string, string | null];
      }),
    );
    const logos: Record<string, string> = {};
    for (const [name, url] of entries) {
      if (url) logos[name] = url;
    }
    setManufacturerLogos(logos);
  };

  const value = useMemo<string>(
    () => question.fact?.raw?.toString() ?? "",
    [question],
  );

  // The "Use customer Switch" sentinel is not a device id: treat it as an empty
  // selection for the NetBox device id parsing / fetching below.
  const deviceIdsValue = value === CUSTOMER_SWITCH_VALUE ? "" : value;

  // Keep the toggle in sync with the stored value so it survives re-renders and
  // section regeneration (which hands us a fresh question with the saved fact).
  useEffect(() => {
    setUseCustomerSwitch(value === CUSTOMER_SWITCH_VALUE);
  }, [value]);

  useEffect(() => {
    if (!isLoaded && devices.length > 0) {
      const savedIds =
        deviceIdsValue
          ?.split(",")
          .map((id) => id.trim())
          .filter(Boolean)
          .map(Number) ?? [];
      const foundDevices = savedIds
        .map((id) => devices.find((d) => d.id === id))
        .filter((d): d is NetBoxDeviceWithInventory => Boolean(d));
      setSelectedDevices(foundDevices);
      setSelectedOptions(foundDevices.map(deviceToOption));
      void loadDeviceDetail(foundDevices.map((d) => d.id));
      setIsLoaded(true);
    }
  }, [devices, isLoaded, question]);

  useEffect(() => {
    if (canFetch)
      getDevices(deviceIdsValue).then(() => fetchAllDevices(devices));
  }, [canFetch, deviceIdsValue]);

  useEffect(() => {
    if (devices.length > 0) fetchAllDevices(devices);
  }, [devices]);

  // The combobox filters by the option label, so include the serial number in
  // it: this makes every device searchable by name AND serial number. The
  // serial is appended only when present.
  const deviceToOption = (
    device: NetBoxDeviceWithInventory,
  ): EuiComboBoxOptionOption<NetBoxDeviceWithInventory> => {
    const serial = device.serial?.trim();
    const base = `${device.name} (${device.site.name})`;
    return {
      label: serial ? `${base} - SN: ${serial}` : base,
      value: device,
    };
  };

  const deviceOptions = useMemo<
    EuiComboBoxOptionOption<NetBoxDeviceWithInventory>[]
  >(() => devices.map(deviceToOption), [devices]);

  const onChange = (
    options: EuiComboBoxOptionOption<NetBoxDeviceWithInventory>[],
  ) => {
    setSelectedOptions(options);
    const selected = options
      .map((o) => o.value)
      .filter((d): d is NetBoxDeviceWithInventory => Boolean(d));
    setSelectedDevices(selected);
    void loadDeviceDetail(selected.map((d) => d.id));
    setValue(selected.map((d) => d.id).join(","));
  };

  const renderTags = (
    tags: Array<{ id: number; name: string; slug: string; color?: string }>,
    highlightSlug?: string,
  ) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {tags.map((tag) => (
        <EuiBadge
          key={tag.id}
          color={
            tag.slug === highlightSlug
              ? "primary"
              : tag.color
                ? tagStyle(tag.color)
                : "hollow"
          }
        >
          {tag.name}
        </EuiBadge>
      ))}
    </div>
  );

  const renderHardwareSummary = (device: NetBoxDeviceWithInventory) => {
    const cf = device.custom_fields;
    if (!cf) return null;
    const memory = cf["vcsa_host_memory"];
    const cpu = cf["vcsa_host_cpu_cores"];
    const storage = cf["Storage"];
    if (!memory && !cpu && !storage) return null;

    const formatStorage = (raw: string | number | null): string => {
      if (!raw) return "-";
      const gb = parseFloat(String(raw));
      if (isNaN(gb)) return String(raw);
      if (gb >= 1024) return `${(gb / 1024).toFixed(1)} TB`;
      return `${gb} GB`;
    };

    const stats = [
      {
        icon: "memory",
        label: translate("anketa.questionnaire.editor.device.stat.memory", {
          defaultMessage: "Memory",
        }),
        value: memory ? `${memory} GiB` : "-",
      },
      {
        icon: "compute",
        label: translate("anketa.questionnaire.editor.device.stat.cpuCores", {
          defaultMessage: "CPU Cores",
        }),
        value: cpu ? `${cpu} Cores` : "-",
      },
      {
        icon: "storage",
        label: translate("anketa.questionnaire.editor.device.stat.storage", {
          defaultMessage: "Storage",
        }),
        value: formatStorage(storage),
      },
    ];

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 1,
          background: T.border,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          overflow: "hidden",
          marginTop: 4,
        }}
      >
        {stats.map(({ icon, label, value: val }) => (
          <div
            key={label}
            style={{
              background: T.card,
              padding: "10px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 4,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                color: T.textMuted,
              }}
            >
              <EuiIcon type={icon} size="s" color={T.blue} />
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {label}
              </span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
              {val}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderPrimaryNics = (device: NetBoxDeviceWithInventory) => {
    if (!device.interfaces || device.interfaces.length === 0) return null;
    const CPE_TAG = "opsanioniccpe";
    const TEN_GB_TYPES = [
      "10gbase-t",
      "10gbase-x-sfpp",
      "10gbase-x-xfp",
      "10gbase-x-sfpplus",
      "10gbase-cx4",
      "10gbase-kr",
    ];
    const taggedNics = device.interfaces.filter((iface) =>
      iface.tags.some((tag) => tag.slug === CPE_TAG),
    );
    const tenGbNics = device.interfaces.filter((iface) =>
      TEN_GB_TYPES.includes(iface.type.value),
    );
    const primaryNics = (taggedNics.length > 0 ? taggedNics : tenGbNics).slice(
      0,
      2,
    );
    if (primaryNics.length === 0) return null;
    const selectionReason =
      taggedNics.length > 0 ? `Tagged: ${CPE_TAG}` : "Fallback: 10Gb";

    return (
      <div style={sectionBox}>
        <div
          style={{
            padding: "8px 12px",
            background: T.surface,
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <EuiIcon type="network" size="m" color={T.blue} />
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: T.textMuted,
            }}
          >
            {translate("anketa.questionnaire.editor.device.primaryNics", {
              defaultMessage: "Primary NICs",
            })}
          </span>
          <EuiBadge color={taggedNics.length > 0 ? "primary" : "warning"}>
            {selectionReason}
          </EuiBadge>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {primaryNics.map((iface, idx) => (
            <div
              key={iface.id}
              style={{
                padding: "10px 14px",
                borderTop: idx > 0 ? `1px solid ${T.border}` : undefined,
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: iface.enabled ? "#00BFB3" : "#98A2B3",
                  marginTop: 5,
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  <EuiLink href={iface.display_url} target="_blank" external>
                    <strong style={{ fontSize: 13 }}>{iface.name}</strong>
                  </EuiLink>
                  {iface.mgmt_only && <EuiBadge color="warning">mgmt</EuiBadge>}
                  <EuiHealth
                    color={iface.enabled ? "success" : "subdued"}
                    style={{ fontSize: 12 }}
                  >
                    {iface.enabled
                      ? translate(
                          "anketa.questionnaire.editor.device.iface.up",
                          { defaultMessage: "Up" },
                        )
                      : translate(
                          "anketa.questionnaire.editor.device.iface.down",
                          { defaultMessage: "Down" },
                        )}
                  </EuiHealth>
                </div>
                <div
                  style={{
                    marginTop: 4,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <div style={metaRow}>
                    <EuiIcon type="tokenInterface" size="s" />
                    <span>{iface.type.label}</span>
                  </div>
                  {iface.mac_address && (
                    <div style={metaRow}>
                      <EuiIcon type="tokenConstant" size="s" />
                      <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                        {iface.mac_address}
                      </span>
                    </div>
                  )}
                  {iface.description && (
                    <div style={metaRow}>
                      <EuiIcon type="editorComment" size="s" />
                      <span>{iface.description}</span>
                    </div>
                  )}
                  {iface.tags.length > 0 && (
                    <div style={{ marginTop: 2 }}>
                      {renderTags(iface.tags, CPE_TAG)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderInterfaces = (device: NetBoxDeviceWithInventory) => {
    if (!device.interfaces || device.interfaces.length === 0) return null;
    return (
      <EuiAccordion
        id={`ifaces-${device.id}`}
        buttonContent={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EuiIcon type="network" size="m" />
            <EuiText size="s">
              {translate("anketa.questionnaire.editor.device.interfaces", {
                defaultMessage: "Interfaces",
              })}
            </EuiText>
            <EuiBadge color="hollow">{device.interfaces.length}</EuiBadge>
          </div>
        }
        paddingSize="s"
      >
        <EuiTable compressed>
          <EuiTableHeader>
            <EuiTableHeaderCell>
              {translate("anketa.questionnaire.editor.device.table.name", {
                defaultMessage: "Name",
              })}
            </EuiTableHeaderCell>
            <EuiTableHeaderCell>
              {translate("anketa.questionnaire.editor.device.table.type", {
                defaultMessage: "Type",
              })}
            </EuiTableHeaderCell>
            <EuiTableHeaderCell>
              {translate("anketa.questionnaire.editor.device.table.mac", {
                defaultMessage: "MAC",
              })}
            </EuiTableHeaderCell>
            <EuiTableHeaderCell>
              {translate("anketa.questionnaire.editor.device.table.status", {
                defaultMessage: "Status",
              })}
            </EuiTableHeaderCell>
          </EuiTableHeader>
          <EuiTableBody>
            {device.interfaces.map((iface) => (
              <EuiTableRow key={iface.id}>
                <EuiTableRowCell>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    {iface.mgmt_only && (
                      <EuiBadge color="warning">mgmt</EuiBadge>
                    )}
                    <EuiLink href={iface.display_url} target="_blank">
                      <EuiText size="xs">{iface.name}</EuiText>
                    </EuiLink>
                  </div>
                </EuiTableRowCell>
                <EuiTableRowCell>
                  <EuiText size="xs">{iface.type.label}</EuiText>
                </EuiTableRowCell>
                <EuiTableRowCell>
                  <EuiText size="xs" style={{ fontFamily: "monospace" }}>
                    {iface.mac_address ?? "-"}
                  </EuiText>
                </EuiTableRowCell>
                <EuiTableRowCell>
                  <EuiHealth color={iface.enabled ? "success" : "subdued"}>
                    {iface.enabled
                      ? translate(
                          "anketa.questionnaire.editor.device.iface.enabled",
                          { defaultMessage: "Enabled" },
                        )
                      : translate(
                          "anketa.questionnaire.editor.device.iface.disabled",
                          { defaultMessage: "Disabled" },
                        )}
                  </EuiHealth>
                </EuiTableRowCell>
              </EuiTableRow>
            ))}
          </EuiTableBody>
        </EuiTable>
      </EuiAccordion>
    );
  };

  const renderRamInventory = (device: NetBoxDeviceWithInventory) => {
    if (!device.inventory || device.inventory.length === 0) return null;
    const ramItems = device.inventory.filter(
      (item) =>
        item.name.toUpperCase().includes("DIMM") ||
        (!item.name.match(/^proc\s*\d+\s*-/i) &&
          item.description.toUpperCase().includes("DDR")),
    );
    if (ramItems.length === 0) return null;

    const grouped = ramItems.reduce<Record<string, typeof ramItems>>(
      (acc, item) => {
        const match = item.name.match(/^(PROC\s*\d+)/i);
        const group = match ? match[1].toUpperCase() : "OTHER";
        if (!acc[group]) acc[group] = [];
        acc[group].push(item);
        return acc;
      },
      {},
    );

    const totalGb = ramItems.reduce((sum, item) => {
      const match = item.name.match(/(\d+)GB/i);
      return sum + (match ? parseInt(match[1], 10) : 0);
    }, 0);

    return (
      <EuiAccordion
        id={`ram-${device.id}`}
        buttonContent={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EuiIcon type="memory" size="m" color={T.blue} />
            <EuiText size="s">
              <strong>
                {translate("anketa.questionnaire.editor.device.stat.memory", {
                  defaultMessage: "Memory",
                })}
              </strong>
            </EuiText>
            <EuiBadge color="primary">
              {translate("anketa.questionnaire.editor.device.ram.total", {
                values: { gb: totalGb },
                defaultMessage: "{{gb}} GB Total",
              })}
            </EuiBadge>
            <EuiBadge color="hollow">
              {translate("anketa.questionnaire.editor.device.ram.dimms", {
                values: { count: ramItems.length },
                defaultMessage: "{{count}} DIMMs",
              })}
            </EuiBadge>
          </div>
        }
        paddingSize="s"
      >
        <EuiFlexGroup direction="column" gutterSize="m">
          {Object.entries(grouped).map(([procSlot, items]) => {
            const slotTotal = items.reduce((sum, item) => {
              const match = item.name.match(/(\d+)GB/i);
              return sum + (match ? parseInt(match[1], 10) : 0);
            }, 0);

            return (
              <EuiFlexItem key={procSlot}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <EuiIcon type="compute" size="s" />
                  <EuiText size="s">
                    <strong>{procSlot}</strong>
                  </EuiText>
                  <EuiBadge color="hollow">{slotTotal} GB</EuiBadge>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: 6,
                  }}
                >
                  {items.map((item) => {
                    const isPopulated = item.name.match(/(\d+)GB/i);
                    const sizeMatch = item.name.match(/(\d+)GB/i);
                    const typeMatch = item.description.match(/(DDR\d?)/i);
                    const speedMatch = item.description.match(/(\d+)MHz/i);

                    return (
                      <EuiToolTip
                        key={item.id}
                        content={
                          item.display_url ? (
                            <EuiLink
                              href={item.display_url}
                              target="_blank"
                              external
                            >
                              {translate(
                                "anketa.questionnaire.editor.device.viewInNetbox",
                                { defaultMessage: "View in NetBox" },
                              )}
                            </EuiLink>
                          ) : undefined
                        }
                      >
                        <div
                          style={{
                            border: `1px solid ${isPopulated ? T.blue : T.border}`,
                            borderRadius: T.radiusSm,
                            padding: "8px 10px",
                            background: isPopulated ? T.blueDim : T.surface,
                            cursor: item.display_url ? "pointer" : "default",
                          }}
                          onClick={() =>
                            item.display_url &&
                            window.open(item.display_url, "_blank")
                          }
                        >
                          <div
                            style={{
                              fontSize: 11,
                              color: T.textMuted,
                              marginBottom: 4,
                            }}
                          >
                            {item.name.match(/DIMM\s*\d+/i)?.[0] ?? item.name}
                          </div>
                          {isPopulated ? (
                            <div
                              style={{
                                display: "flex",
                                gap: 4,
                                flexWrap: "wrap",
                              }}
                            >
                              <EuiBadge color="primary">
                                {sizeMatch?.[1]} GB
                              </EuiBadge>
                              {typeMatch && (
                                <EuiBadge color="hollow">
                                  {typeMatch?.[1]}
                                </EuiBadge>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: T.textMuted }}>
                              {translate(
                                "anketa.questionnaire.editor.device.ram.empty",
                                { defaultMessage: "Empty" },
                              )}
                            </span>
                          )}
                          {speedMatch && (
                            <div style={{ ...metaRow, marginTop: 4 }}>
                              <EuiIcon type="clock" size="s" />
                              <span style={{ fontSize: 11 }}>
                                {speedMatch?.[1]} MHz
                              </span>
                            </div>
                          )}
                          {item.manufacturer && (
                            <div
                              style={{
                                fontSize: 11,
                                color: T.textMuted,
                                marginTop: 2,
                              }}
                            >
                              {item.manufacturer.name}
                            </div>
                          )}
                          {item.part_id && (
                            <div
                              style={{
                                fontFamily: "monospace",
                                fontSize: 10,
                                color: T.textMuted,
                                marginTop: 2,
                                wordBreak: "break-all",
                              }}
                            >
                              {item.part_id}
                            </div>
                          )}
                        </div>
                      </EuiToolTip>
                    );
                  })}
                </div>
              </EuiFlexItem>
            );
          })}
        </EuiFlexGroup>
      </EuiAccordion>
    );
  };

  const renderCpuInventory = (device: NetBoxDeviceWithInventory) => {
    if (!device.inventory || device.inventory.length === 0) return null;
    const cpuItems = device.inventory.filter(
      (item) =>
        !item.name.toUpperCase().includes("DIMM") &&
        (item.name.match(/^proc\s*\d+\s*-/i) ||
          item.description.toLowerCase().includes("cores")),
    );
    if (cpuItems.length === 0) return null;

    const totalCores = cpuItems.reduce((sum, item) => {
      const m = item.description.match(/(\d+)\s*Cores/i);
      return sum + (m ? parseInt(m[1], 10) : 0);
    }, 0);
    const totalThreads = cpuItems.reduce((sum, item) => {
      const m = item.description.match(/(\d+)\s*Threads/i);
      return sum + (m ? parseInt(m[1], 10) : 0);
    }, 0);

    return (
      <EuiAccordion
        id={`cpu-${device.id}`}
        buttonContent={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EuiIcon type="compute" size="m" color={T.blue} />
            <EuiText size="s">
              <strong>
                {translate("anketa.questionnaire.editor.device.processors", {
                  defaultMessage: "Processors",
                })}
              </strong>
            </EuiText>
            <EuiBadge color="primary">
              {cpuItems.length} CPU{cpuItems.length > 1 ? "s" : ""}
            </EuiBadge>
            <EuiBadge color="hollow">
              {totalCores}C / {totalThreads}T
            </EuiBadge>
          </div>
        }
        paddingSize="s"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {cpuItems.map((item) => {
            const socketMatch = item.name.match(/^(Proc\s*\d+)/i);
            const modelMatch = item.name.match(/^Proc\s*\d+\s*-\s*(.+)$/i);
            const coresMatch = item.description.match(/(\d+)\s*Cores/i);
            const threadsMatch = item.description.match(/(\d+)\s*Threads/i);
            const speedMatch = item.name.match(/@\s*([\d.]+\s*GHz)/i);
            const familyMatch = item.name.match(
              /(Xeon|Core|EPYC|Ryzen|Opteron)/i,
            );

            const socket =
              socketMatch?.[1] ??
              translate("anketa.questionnaire.editor.device.unknownSocket", {
                defaultMessage: "Unknown Socket",
              });
            const model = modelMatch?.[1] ?? item.name;
            const cores = coresMatch?.[1] ?? "-";
            const threads = threadsMatch?.[1] ?? "-";
            const speed = speedMatch?.[1] ?? null;
            const family = familyMatch?.[1] ?? null;

            return (
              <div
                key={item.id}
                style={{
                  border: `1px solid ${T.blue}`,
                  borderRadius: T.radius,
                  padding: "12px 14px",
                  background: T.blueDim,
                  cursor: item.display_url ? "pointer" : "default",
                }}
                onClick={() =>
                  item.display_url && window.open(item.display_url, "_blank")
                }
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 8,
                  }}
                >
                  <EuiBadge color="primary">{socket}</EuiBadge>
                  {family && <EuiBadge color="hollow">{family}</EuiBadge>}
                  <EuiHealth color="success">
                    {(item as any).status.label}
                  </EuiHealth>
                  {item.display_url && (
                    <EuiLink
                      href={item.display_url}
                      target="_blank"
                      external
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EuiText size="xs">NetBox</EuiText>
                    </EuiLink>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 6,
                  }}
                >
                  <EuiIcon type="compute" size="s" />
                  <strong style={{ fontSize: 13 }}>{model}</strong>
                </div>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div style={metaRow}>
                    <EuiIcon type="branch" size="s" />
                    <span>
                      {translate(
                        "anketa.questionnaire.editor.device.cpu.cores",
                        { values: { count: cores }, defaultMessage: "{{count}} Cores" },
                      )}
                    </span>
                  </div>
                  <div style={metaRow}>
                    <EuiIcon type="gear" size="s" />
                    <span>
                      {translate(
                        "anketa.questionnaire.editor.device.cpu.threads",
                        {
                          values: { count: threads },
                          defaultMessage: "{{count}} Threads",
                        },
                      )}
                    </span>
                  </div>
                  {speed && (
                    <div style={metaRow}>
                      <EuiIcon type="clock" size="s" />
                      <span>{speed}</span>
                    </div>
                  )}
                </div>

                {(item.manufacturer || item.part_id) && (
                  <div
                    style={{
                      marginTop: 6,
                      display: "flex",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    {item.manufacturer && (
                      <div style={metaRow}>
                        <EuiIcon type="tag" size="s" />
                        <EuiLink
                          href={item.manufacturer.display_url}
                          target="_blank"
                        >
                          <EuiText size="xs" color="subdued">
                            {item.manufacturer.name}
                          </EuiText>
                        </EuiLink>
                      </div>
                    )}
                    {item.part_id && (
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: 10,
                          color: T.textMuted,
                        }}
                      >
                        {item.part_id}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </EuiAccordion>
    );
  };

  const renderInventory = (device: NetBoxDeviceWithInventory) => {
    if (!device.inventory || device.inventory.length === 0) return null;
    return (
      <EuiAccordion
        id={`inv-${device.id}`}
        buttonContent={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EuiIcon type="package" size="m" />
            <EuiText size="s">
              {translate("anketa.questionnaire.editor.device.inventory", {
                defaultMessage: "Inventory",
              })}
            </EuiText>
            <EuiBadge color="hollow">{device.inventory.length}</EuiBadge>
          </div>
        }
        paddingSize="s"
      >
        <EuiTable compressed>
          <EuiTableHeader>
            <EuiTableHeaderCell>
              {translate("anketa.questionnaire.editor.device.table.name", {
                defaultMessage: "Name",
              })}
            </EuiTableHeaderCell>
            <EuiTableHeaderCell>
              {translate(
                "anketa.questionnaire.editor.device.table.manufacturer",
                { defaultMessage: "Manufacturer" },
              )}
            </EuiTableHeaderCell>
            <EuiTableHeaderCell>
              {translate("anketa.questionnaire.editor.device.table.serial", {
                defaultMessage: "Serial",
              })}
            </EuiTableHeaderCell>
            <EuiTableHeaderCell>
              {translate(
                "anketa.questionnaire.editor.device.table.description",
                { defaultMessage: "Description" },
              )}
            </EuiTableHeaderCell>
          </EuiTableHeader>
          <EuiTableBody>
            {device.inventory.map((item) => (
              <EuiTableRow key={item.id}>
                <EuiTableRowCell>
                  <EuiLink href={item.display_url} target="_blank">
                    <EuiText size="xs">{item.name}</EuiText>
                  </EuiLink>
                </EuiTableRowCell>
                <EuiTableRowCell>
                  {item.manufacturer ? (
                    <EuiLink
                      href={item.manufacturer.display_url}
                      target="_blank"
                    >
                      <EuiText size="xs">{item.manufacturer.name}</EuiText>
                    </EuiLink>
                  ) : (
                    "-"
                  )}
                </EuiTableRowCell>
                <EuiTableRowCell>
                  <EuiText size="xs">{item.serial || "-"}</EuiText>
                </EuiTableRowCell>
                <EuiTableRowCell>
                  <EuiText size="xs">{item.description || "-"}</EuiText>
                </EuiTableRowCell>
              </EuiTableRow>
            ))}
          </EuiTableBody>
        </EuiTable>
      </EuiAccordion>
    );
  };

  const renderDeviceCard = (baseDevice: NetBoxDeviceWithInventory) => {
    const detail = detailMap[baseDevice.id];
    const device = detail ? { ...baseDevice, ...detail } : baseDevice;
    const isDetailLoading =
      type === "server" && detailLoading.has(baseDevice.id);
    const statusColor = statusColorMap[device.status.value] ?? "subdued";
    const manufacturerName = device.device_type.manufacturer.name;
    const logoUrl = manufacturerLogos[manufacturerName];

    return (
      <div
        style={{
          border: `1px solid ${T.borderStrong}`,
          borderRadius: T.radius,
          overflow: "hidden",
          background: T.card,
          boxShadow: T.shadow,
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            borderBottom: `1px solid ${T.border}`,
            background: T.surface,
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <div style={{ flexShrink: 0 }}>
            {logoUrl ? (
              <EuiToolTip content={manufacturerName}>
                <img
                  src={logoUrl}
                  alt={manufacturerName}
                  style={{ width: 40, height: 40, objectFit: "contain" }}
                />
              </EuiToolTip>
            ) : (
              <EuiAvatar name={manufacturerName} size="l" color={AVATAR_BLUE} />
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <EuiLink href={device.display_url} target="_blank" external>
                <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
                  {device.name}
                </span>
              </EuiLink>
              <EuiHealth color={statusColor}>{device.status.label}</EuiHealth>
            </div>
            <EuiLink href={device.device_type.display_url} target="_blank">
              <span style={{ fontSize: 12, color: T.textMuted }}>
                {manufacturerName} · {device.device_type.model}
              </span>
            </EuiLink>
          </div>
        </div>

        <div style={{ padding: "14px 16px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: "8px 16px",
              marginBottom: 12,
            }}
          >
            <div style={metaRow}>
              <EuiIcon type="mapMarker" size="m" color={T.textMuted} />
              <EuiLink href={device.site.display_url} target="_blank">
                <span style={{ fontSize: 13 }}>{device.site.name}</span>
              </EuiLink>
            </div>

            <div style={metaRow}>
              <EuiIcon type="tag" size="m" color={T.textMuted} />
              <EuiLink href={device.role.display_url} target="_blank">
                <span style={{ fontSize: 13 }}>{device.role.name}</span>
              </EuiLink>
            </div>

            {device.serial && (
              <div style={metaRow}>
                <EuiIcon type="document" size="m" color={T.textMuted} />
                <span style={{ fontSize: 13 }}>S/N: {device.serial}</span>
              </div>
            )}

            {device.platform && (
              <div style={metaRow}>
                <EuiIcon type="layers" size="m" color={T.textMuted} />
                <EuiLink href={device.platform.display_url} target="_blank">
                  <span style={{ fontSize: 13 }}>{device.platform.name}</span>
                </EuiLink>
              </div>
            )}
          </div>

          {device.tags.length > 0 && (
            <div style={{ marginBottom: 12 }}>{renderTags(device.tags)}</div>
          )}
          {renderHardwareSummary(device)}

          {isDetailLoading ? (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 14px",
                ...sectionBox,
              }}
            >
              <EuiLoadingSpinner size="m" />
              <EuiText size="s" color="subdued">
                {translate("anketa.questionnaire.editor.device.loadingDetail", {
                  defaultMessage: "Loading hardware detail…",
                })}
              </EuiText>
            </div>
          ) : (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                ...sectionBox,
              }}
            >
              {renderPrimaryNics(device)}
              {device.interfaces && device.interfaces.length > 0 && (
                <div style={{ borderTop: `1px solid ${T.border}` }}>
                  {renderInterfaces(device)}
                </div>
              )}
              {device.inventory && device.inventory.length > 0 && (
                <>
                  <div style={{ borderTop: `1px solid ${T.border}` }}>
                    {renderCpuInventory(device)}
                  </div>
                  <div style={{ borderTop: `1px solid ${T.border}` }}>
                    {renderRamInventory(device)}
                  </div>
                  <div style={{ borderTop: `1px solid ${T.border}` }}>
                    {renderInventory(device)}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <EuiFlexItem grow style={{ minWidth: 200 }}>
      <EuiFlexGroup direction="row">
        {type === "switch" && (
          <EuiFlexItem>
            <EuiSwitch
              label={translate(
                "anketa.questionnaire.editor.device.useCustomerSwitch",
                { defaultMessage: "Use customer Switch" },
              )}
              checked={useCustomerSwitch}
              disabled={question.readonly}
              onChange={() => {
                const next = !useCustomerSwitch;
                setUseCustomerSwitch(next);
                if (next) {
                  // Customer-provided switch: drop any NetBox selection and
                  // persist the sentinel so the choice survives re-rendering.
                  setSelectedDevices([]);
                  setSelectedOptions([]);
                  setValue(CUSTOMER_SWITCH_VALUE);
                } else {
                  setValue("");
                }
              }}
            />
          </EuiFlexItem>
        )}
        {!useCustomerSwitch && (
          <EuiFlexItem>
            <EuiComboBox
              placeholder={translate(
                "anketa.questionnaire.editor.device.select.placeholder",
                { defaultMessage: "Select devices" },
              )}
              options={deviceOptions}
              selectedOptions={selectedOptions}
              onChange={onChange}
              isClearable
              clearOnBlur
              isLoading={!isLoaded}
              isDisabled={question.readonly}
              isInvalid={invalid}
            />
            {noneFree && selectedDevices.length === 0 && (
              <>
                <EuiSpacer size="xs" />
                <EuiText size="xs" color="subdued">
                  <p>
                    {translate(
                      "anketa.questionnaire.editor.device.noneFree",
                      {
                        defaultMessage:
                          'No free devices of this kind in NetBox - every matching device is tagged "opsanioinuse".',
                      },
                    )}
                  </p>
                </EuiText>
              </>
            )}

            {selectedDevices.length > 0 && (
              <>
                <EuiSpacer size="l" />
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  {selectedDevices.map((device) => (
                    <div key={device.id}>{renderDeviceCard(device)}</div>
                  ))}
                </div>
              </>
            )}
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    </EuiFlexItem>
  );
};
